import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { attendanceErrorMessage } from "@/lib/attendanceErrors";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { MapPin } from "lucide-react";

export default function AdminAttendance() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/attendance/admin/overview");
      setOverview(data);
    } catch (err) {
      toast.error(attendanceErrorMessage(err, "Failed to load attendance overview"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function setAddon(enabled) {
    if (!userId.trim()) {
      toast.error("Enter a user id");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/attendance/admin/users/${encodeURIComponent(userId.trim())}/addon`, {
        enabled,
      });
      toast.success(enabled ? "Add-on enabled" : "Add-on revoked");
      setUserId("");
      await load();
    } catch (err) {
      toast.error(attendanceErrorMessage(err, "Failed to update add-on"));
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" role="status" aria-live="polite">
        <div
          className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"
          aria-hidden="true"
        />
        <span className="sr-only">Loading</span>
      </div>
    );
  }

  const totals = overview?.totals || {};

  return (
    <div className="p-6 space-y-6 animate-fade-in" data-testid="admin-attendance-page">
      <div>
        <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
          <MapPin size={22} className="text-amber-400" aria-hidden="true" />
          Smart Attendance
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Platform overview and add-on grants for customer accounts.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          ["Sites", totals.sites],
          ["Open now", totals.open_sessions],
          ["Active QRs", totals.active_tokens],
          ["Entitled users", totals.entitled_users],
        ].map(([label, value]) => (
          <Card key={label} className="card-hover">
            <CardContent className="pt-5">
              <p className="text-xs text-muted-foreground">{label}</p>
              <p className="text-2xl font-heading font-bold mt-1">{value ?? 0}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="card-hover">
        <CardHeader>
          <CardTitle className="text-lg font-heading">Grant / revoke add-on</CardTitle>
          <CardDescription>
            Enable or remove Smart Attendance for a user by their account id.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2 items-end">
          <div className="space-y-2 flex-1 min-w-[200px] max-w-md">
            <Label htmlFor="admin-attendance-user-id">User id</Label>
            <Input
              id="admin-attendance-user-id"
              placeholder="User id"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              data-testid="admin-attendance-user-id"
            />
          </div>
          <Button disabled={busy} className="btn-glow" onClick={() => setAddon(true)} data-testid="admin-attendance-enable">
            Enable
          </Button>
          <Button disabled={busy} variant="outline" onClick={() => setAddon(false)}>
            Revoke
          </Button>
        </CardContent>
      </Card>

      <Card className="card-hover">
        <CardHeader>
          <CardTitle className="text-lg font-heading">Recent sites</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {(overview?.sites || []).length === 0 && (
            <p className="text-sm text-muted-foreground">No attendance sites yet.</p>
          )}
          {(overview?.sites || []).map((s) => (
            <div
              key={s.id}
              className="flex items-center justify-between gap-3 py-2 border-b border-border last:border-0 text-sm"
              data-testid="admin-attendance-site-row"
            >
              <div>
                <p className="font-medium flex flex-wrap items-center gap-2">
                  {s.name}
                  <Badge variant="outline" className="text-[10px]">
                    {s.enabled ? "Enabled" : "Disabled"}
                  </Badge>
                </p>
                <p className="text-xs text-muted-foreground">
                  {s.owner_email || s.owner_id} · {s.open_session_count ?? 0} open
                </p>
              </div>
              <p className="text-xs text-muted-foreground whitespace-nowrap">
                {s.created_at ? new Date(s.created_at).toLocaleDateString() : ""}
              </p>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
